import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasPermission } from '@entities/access';
import { useAuth } from '@entities/auth';
import { useBalance } from '@features/balance';
import { formatRbacError } from '@entities/withdrawal';
import { useWithdrawalActions, useWithdrawalsList, WithdrawalsLockBanner } from '@features/withdrawal';
import { useApiServices } from '@shared/lib/use-api-services';
import { WithdrawalActions } from '@widgets/withdrawal/withdrawal-actions';
import { WithdrawalsTable } from '@widgets/withdrawal/withdrawals-table';
import { AdminWithdrawalsTable } from '@widgets/withdrawal/admin-withdrawals-table';
import { CabinetTablePageSkeleton, StatusBanner } from '@shared/ui';
import { WithdrawalLimitsPanel } from '@widgets/withdrawal/withdrawal-limits';

export function WithdrawalsPage() {
  const { t } = useTranslation('cabinet');
  const { t: tRbac } = useTranslation('rbac');
  const { user } = useAuth();
  const services = useApiServices();
  const isAdmin = hasPermission(user, 'withdrawal.read_any');
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState('');
  const [searchParams] = useSearchParams();
  const { withdrawals, pagination, isLoading, isValidating, error, mutate } = useWithdrawalsList(page, 20, isAdmin, stateFilter);
  const {
    balance,
    isLoading: isBalanceLoading,
    isValidating: isBalanceValidating,
    error: balanceError,
    mutate: mutateBalance,
  } = useBalance();
  const {
    cancelWithdrawal,
    processAdminWithdrawal,
    cancelAdminWithdrawal,
    isLoading: isActionLoading,
  } = useWithdrawalActions();

  const handleRefresh = () => {
    void mutate(undefined, { revalidate: true });
    void mutateBalance(undefined, { revalidate: true });
  };

  const isTableRefreshing = isValidating && withdrawals.length > 0;

  const actions = (
    <WithdrawalActions
      availableBalance={balance?.availableBalance == null ? null : Number(balance.availableBalance)}
      totalBalance={balance == null ? null : balance.totalBalance}
      assetBalances={balance?.assetBalances ?? []}
      autoWithdraw={balance?.autoWithdraw ?? null}
      isBalanceLoading={isBalanceLoading || isBalanceValidating}
      balanceError={balanceError}
      onRetryBalance={() => void mutateBalance()}
      onUpdate={handleRefresh}
    />
  );

  const infoBanner = (
    <>
      <WithdrawalsLockBanner />
      {error && withdrawals.length ? <StatusBanner>{t('withdrawals.stale')}</StatusBanner> : null}
      {isAdmin ? null : <WithdrawalLimitsPanel />}
      <div className="surface-card flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <p className="font-sans text-sm leading-snug text-foreground">{t('withdrawals.info')}</p>
        {actions}
      </div>
    </>
  );

  if (isLoading && !withdrawals.length) {
    return (
      <div className="page-stack">
        {infoBanner}
        <CabinetTablePageSkeleton showInfoBanner={false} showStats={false} tableRows={8} />
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="page-stack">
        {infoBanner}
        <AdminWithdrawalsTable
          withdrawals={withdrawals}
          pagination={pagination}
          isLoading={isLoading}
          isRefreshing={isTableRefreshing}
          isActionLoading={isActionLoading}
          onRefresh={handleRefresh}
          onPageChange={setPage}
          stateFilter={stateFilter}
          onStateFilter={(next) => {
            setStateFilter(next);
            setPage(1);
          }}
          actorId={user?.id}
          canExecute={hasPermission(user, 'withdrawal.execute')}
          canReject={hasPermission(user, 'withdrawal.reject')}
          canEmergency={hasPermission(user, 'withdrawal.emergency')}
          canReadAudit={hasPermission(user, 'audit.read')}
          onProcess={processAdminWithdrawal}
          onCancel={cancelAdminWithdrawal}
          onReconcile={async (id, reason, expectedStateVersion) => {
            try {
              await services.withdrawals.reconcileWithdrawal(id, { reason, expectedStateVersion });
              handleRefresh();
              return { success: true };
            } catch (error) {
              handleRefresh();
              return { success: false, error: formatRbacError(error, tRbac, t('withdrawals.reconcile.error')) };
            }
          }}
          onCreateGrant={async (payload) => {
            try {
              await services.rbac.createEmergencyGrant({
                withdrawalId: payload.withdrawalId,
                overrides: payload.overrides,
                idempotencyKey: payload.idempotencyKey,
                ...payload.command,
              });
              handleRefresh();
              return { success: true };
            } catch (error) {
              return { success: false, error: formatRbacError(error, tRbac, t('withdrawals.actions.processError')) };
            }
          }}
          loadError={Boolean(error)}
          onRevokeGrant={async (payload) => {
            try {
              await services.rbac.revokeEmergencyGrant(payload.id, payload.command);
              handleRefresh();
              return { success: true };
            } catch (error) {
              return { success: false, error: formatRbacError(error, tRbac, t('withdrawals.actions.processError')) };
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="page-stack">
      {infoBanner}
      <WithdrawalsTable
        withdrawals={withdrawals}
        pagination={pagination}
        isLoading={isLoading}
        isRefreshing={isTableRefreshing}
        isActionLoading={isActionLoading}
        onRefresh={handleRefresh}
        onPageChange={setPage}
        onCancel={cancelWithdrawal}
        openId={searchParams.get('withdrawal')}
        loadError={Boolean(error)}
      />
    </div>
  );
}
